import { Document, Image, Link, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { format } from "date-fns";
import { PaciraLogoBlack, ZilrettaLogoPdf } from "src/assets";
import { LocalCoverageResponseType } from "./types";
import CoverageHeaderPdf from "src/components/pdf/header/pdfRender";


type PDFReportProps = {
  zipCode: string;
  getLocalCoverage?: LocalCoverageResponseType;
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    color: "#000000",
    fontSize: 10,
    padding: 0,
    paddingBottom: 170,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  body: {
    marginHorizontal: 0,
    flex: 1,
    borderColor: "#697684",
    borderWidth: 1,
    borderTop: 0,
  },
  heading: {
    backgroundColor: "#000000",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 600,
    paddingHorizontal: 13,
    paddingVertical: 5,
    margin: 0,
    borderColor: "#697684",
    borderBottomWidth: 1,
  },
  superscript: {
    fontSize: 8,
    verticalAlign: "super",
  },

  table: {
    borderWidth: undefined,
    margin: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  oddRow: {
      backgroundColor:"#f5f5f5",
  },
  tableCell: {
    borderBottomWidth: 0,
    padding: 6,
    width: "25%",
  },
  tableHeaderRow: {
    backgroundColor: "#435364",
  },
  tableHeaderCell: {
    color: "#ffffff",
    fontWeight: 700,
  },
  emptyHeaderCell:{
    backgroundColor: "#FFFFFF",
  },
  tableHeaderRowAlt: {
    backgroundColor: "#00bcb4",
  },
  tableHeaderAltCell: {
    color: "#ffffff",
    fontWeight: 600,
  },
  valueTableCell: {
    borderBottomColor: "#dee2e6",
    borderBottomWidth: .5,
  },

  questions: {
    backgroundColor: "#697684",
    color: "#ffffff",
    fontSize: 9,
    fontWeight: 700,
    paddingVertical: 5,
    textAlign: "center",
  },
  safetyInfo: {
    borderColor: "#697684",
    borderWidth: 1,
    backgroundColor: "#eff5f7",
    paddingHorizontal: 7,
    paddingVertical: 6,
  },
  safetyTitle: {
    fontSize: 9,
    fontWeight: 700,
    marginBottom: 6,
  },
  safetyLink: {
    color: "#0cb5b3",
    textDecoration: "none",
  },
  safetyText: {
    color: "#435364",
    fontSize: 8,
    lineHeight: 1.4,
  },

  footer:{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      width: '100%',
  },
  footerLeft: {
    flex: 1,
    paddingRight: 8,
  },
  footerText: {
    fontSize: 8,
    paddingLeft: 5,
    marginBottom: 4,
  },
  footerBold: {
    fontWeight: 700,
  },
  footerKeyLabel: {
    color: "#009c9c",
    fontWeight: 700,
  },
  footerKeyBracket: {
    color: "#d81b60",
    fontWeight: 700,
  },
  footerLogos:{
      flexDirection:"row",
      padding: 6,
  },
  footerLogoLeft: {
    width: 70, height: 27,
    marginTop: 8,
  },
  footerRight: {
      alignItems: "flex-end",
      justifyContent: "flex-start",
  },
  footerLogoRight: {
    width: 150, height: 63,
    marginTop: 1,
  },
});

const PDFReport = ({ zipCode, getLocalCoverage }: PDFReportProps) => {
  const resolvedZipCode =
    zipCode.length === 0 ? "[Please enter search criteria above]" : zipCode.toString();
  const contactInfo = getLocalCoverage?.data
    ? `${getLocalCoverage.data.name} ${getLocalCoverage.data.email}`
    : "";
  const formularies = getLocalCoverage?.data?.formularies ?? [];

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View fixed>
                    <CoverageHeaderPdf />
                    <Text style={styles.heading} fixed>
                        ZILRETTA<Text style={styles.superscript}>®</Text> Coverage at {resolvedZipCode}
                    </Text>
                </View>
                <View style={styles.body}>
                    <View style={styles.table}>
                        <View fixed>
                            <View style={[styles.tableRow, styles.tableHeaderRow]}>
                                <Text style={[styles.tableCell, styles.tableHeaderCell, styles.emptyHeaderCell]}></Text>
                                <Text style={[styles.tableCell, styles.tableHeaderCell]}>Channel</Text>
                                <Text style={[styles.tableCell, styles.tableHeaderCell]}>Medical Coverage</Text>
                                <Text style={[styles.tableCell, styles.tableHeaderCell]}>Pharmacy Coverage</Text>
                            </View>
                            <View style={[styles.tableRow, styles.tableHeaderRowAlt]}>
                                <Text style={[styles.tableCell, styles.tableHeaderAltCell]}>Medicare Part B:</Text>
                                <Text style={[styles.tableCell, styles.tableHeaderAltCell]}>Medicare</Text>
                                <Text style={[styles.tableCell, styles.tableHeaderAltCell]}>Covered Without Restrictions</Text>
                                <Text style={[styles.tableCell, styles.tableHeaderAltCell]}>
                                    See Medical Coverage
                                </Text>
                            </View>
                        </View>
                        {formularies.map((kase, index) => (
                            <View style={[styles.tableRow, index % 2 === 0 && styles.oddRow].filter(Boolean) as any} key={`${kase.payer_name}-${index}`}  wrap={false}>
                                <Text style={[styles.tableCell, styles.valueTableCell]}>{kase.payer_name}</Text>
                                <Text style={[styles.tableCell, styles.valueTableCell]}>{kase.channel}</Text>
                                <Text style={[styles.tableCell, styles.valueTableCell]}>{kase.medical_coverage}</Text>
                                <Text style={[styles.tableCell, styles.valueTableCell]}>{kase.pharmacy_coverage}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={styles.questions}>
                        QUESTIONS? CONTACT YOUR FIELD REIMBURSEMENT MANAGER: {contactInfo}
                    </Text>
                    <View style={styles.safetyInfo}>
                        <Text style={styles.safetyTitle}>
                            Please see Important Safety Information and full Prescribing Information available at{" "}
                            <Link src="https://www.zilrettapro.com/safety-data/" style={styles.safetyLink}>
                                www.zilrettapro.com/safety-data/
                            </Link>
                            .
                        </Text>
                        <Text style={styles.safetyText}>
                            The information provided represents no statement, promise, or guarantee by Pacira BioSciences, Inc.,
                            concerning levels of reimbursement, payment, or charge.{"\n"}
                            Please follow your standard procedure for conducting a benefit investigation for ZILRETTA.{"\n"}
                            Consult your payer organization with regard to local or actual coverage, reimbursement policies, and
                            determination processes. Information is subject to change without notice. Nothing herein may be construed
                            as an endorsement, approval, recommendation, representation, or warranty of any kind by any plan or
                            insurer referenced herein. This communication is solely the responsibility of Pacira BioSciences, Inc.
                        </Text>
                    </View>
                    <View style={styles.footerLogos}>
                        <View style={styles.footerLeft}>
                            <Text style={styles.footerText}>
                                <Text style={styles.footerBold}>Created on</Text> {format(new Date(), "MMMM dd, yyyy")}
                            </Text>
                            <Text style={styles.footerText}>1. Coverage data provided by Managed Markets Insight & Technology, LLC.</Text>
                            <Text style={styles.footerText}>
                                <Text style={styles.footerKeyBracket}>[</Text>
                                <Text style={styles.footerKeyLabel}>KEY:</Text> <Text style={styles.footerBold}>PA:</Text> Prior
                                Authorization; <Text style={styles.footerBold}>IACS:</Text> Intra-Articular Corticosteroid.
                                <Text style={styles.footerKeyBracket}>]</Text>
                            </Text>
                            <Image src={PaciraLogoBlack} style={styles.footerLogoLeft} />
                        </View>
                        <View style={styles.footerRight}>
                            <Image src={ZilrettaLogoPdf} style={styles.footerLogoRight} />
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
  );
};

export default PDFReport;
